import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Globe, Building2, ArrowRight, Loader2, Eye, EyeOff, Shield, Lock, Wifi } from 'lucide-react';
import axios from 'axios';

import { API } from '../utils/api';
import { APP_VERSION } from '../utils/version';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);
  const [setupLoading, setSetupLoading] = useState(true);
  const [appSettings, setAppSettings] = useState({
    app_name: 'Asset Commander',
    app_byline: 'LLC Management',
    footer_text: 'Asset Commander',
    footer_copyright: 'Global LLC Management',
    app_version: '1.0.0'
  });
  const [publicStats, setPublicStats] = useState({
    llc_count: 0,
    investor_count: 0,
    transaction_count: 0
  });
  const [clientIP, setClientIP] = useState('');
  const [wasAutoLoggedOut, setWasAutoLoggedOut] = useState(false);
  const { login, verifyOtp, resendOtp, user } = useAuth();
  const navigate = useNavigate();

  // OTP challenge state
  const [otpStep, setOtpStep] = useState(false);
  const [otpToken, setOtpToken] = useState(null);
  const [otpMaskedEmail, setOtpMaskedEmail] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [otpRememberDevice, setOtpRememberDevice] = useState(true);
  const [otpResendIn, setOtpResendIn] = useState(0);

  useEffect(() => {
    if (user) {
      navigate('/');
    }
  }, [user, navigate]);

  useEffect(() => {
    // Check if user was auto logged out due to inactivity
    const autoLogoutFlag = localStorage.getItem('wasAutoLoggedOut');
    if (autoLogoutFlag === 'true') {
      setWasAutoLoggedOut(true);
      // Clear the flag after reading it
      localStorage.removeItem('wasAutoLoggedOut');
    }
  }, []);

  useEffect(() => {
    // Load saved credentials if "Remember me" was checked
    const savedEmail = localStorage.getItem('rememberedEmail');
    const savedPassword = localStorage.getItem('rememberedPassword');
    if (savedEmail && savedPassword) {
      setEmail(savedEmail);
      setPassword(savedPassword);
      setRememberMe(true);
    }
  }, []);

  useEffect(() => {
    // Fetch public settings, stats, and setup superadmin
    const initialize = async () => {
      try {
        // Fetch app settings
        const settingsRes = await axios.get(`${API}/settings/public`);
        setAppSettings(settingsRes.data);
      } catch (error) {
        console.warn('Login: failed to load public settings, using defaults', error);
      }
      
      try {
        // Fetch public stats
        const statsRes = await axios.get(`${API}/stats/public`);
        setPublicStats(statsRes.data);
      } catch (error) {
        console.warn('Login: failed to load public stats, using defaults', error);
      }
      
      try {
        // Fetch client IP
        const ipRes = await axios.get('https://api.ipify.org?format=json');
        setClientIP(ipRes.data.ip);
      } catch (error) {
        // Fallback - try to get from backend
        try {
          const ipRes = await axios.get(`${API}/client-ip`);
          setClientIP(ipRes.data.ip);
        } catch {
          setClientIP('Unknown');
        }
      }
      
      try {
        // Setup superadmin on first load
        await axios.post(`${API}/setup/superadmin`);
      } catch (error) {
        console.log('Setup check:', error.response?.data?.message || 'Setup complete');
      } finally {
        setSetupLoading(false);
      }
    };
    initialize();
  }, []);

  // Resend cooldown timer
  useEffect(() => {
    if (otpResendIn <= 0) return;
    const t = setInterval(() => {
      setOtpResendIn((s) => (s > 0 ? s - 1 : 0));
    }, 1000);
    return () => clearInterval(t);
  }, [otpResendIn]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const result = await login(email, password);

      // Save or clear credentials based on "Remember me"
      if (rememberMe) {
        localStorage.setItem('rememberedEmail', email);
        localStorage.setItem('rememberedPassword', password);
      } else {
        localStorage.removeItem('rememberedEmail');
        localStorage.removeItem('rememberedPassword');
      }

      if (result && result.otp_required) {
        setOtpToken(result.otp_token);
        setOtpMaskedEmail(result.masked_email || '');
        setOtpResendIn(result.resend_available_in || 60);
        setOtpStep(true);
        toast.success(`Code sent to ${result.masked_email || 'your email'}`);
      } else {
        setWasAutoLoggedOut(false);
        localStorage.removeItem('wasAutoLoggedOut');
        toast.success('Welcome back!');
        navigate('/');
      }
    } catch (error) {
      console.error('Login error:', error);
      const errMsg = error.response?.data?.detail || error.response?.data || error.message || 'Login failed';
      toast.error(typeof errMsg === 'string' ? errMsg : 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    if (!otpCode || otpCode.length < 6) {
      toast.error('Enter the 6-digit code from your email');
      return;
    }
    setLoading(true);
    try {
      await verifyOtp(otpToken, otpCode, otpRememberDevice);
      setWasAutoLoggedOut(false);
      localStorage.removeItem('wasAutoLoggedOut');
      toast.success('Welcome back!');
      navigate('/');
    } catch (error) {
      const errMsg = error.response?.data?.detail || error.message || 'Verification failed';
      toast.error(typeof errMsg === 'string' ? errMsg : 'Verification failed');
      // If code is invalid/expired, reset the otp flow so user starts over
      if (error.response?.status === 429 || (typeof errMsg === 'string' && errMsg.toLowerCase().includes('expired'))) {
        setOtpStep(false);
        setOtpToken(null);
        setOtpCode('');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (otpResendIn > 0) return;
    try {
      const data = await resendOtp(otpToken);
      setOtpToken(data.otp_token);
      setOtpMaskedEmail(data.masked_email || otpMaskedEmail);
      setOtpResendIn(data.resend_available_in || 60);
      setOtpCode('');
      toast.success('New code sent');
    } catch (error) {
      const errMsg = error.response?.data?.detail || 'Failed to resend code';
      toast.error(typeof errMsg === 'string' ? errMsg : 'Failed to resend code');
    }
  };

  const handleBackToLogin = () => {
    setOtpStep(false);
    setOtpCode('');
    setOtpToken(null);
  };

  if (setupLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900">
        <Loader2 className="w-8 h-8 animate-spin text-amber-700" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <div className="flex flex-1">
        {/* Left side - Hero Image */}
        <div 
          className="hidden lg:flex lg:w-1/2 relative bg-slate-900"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1650030259117-cb2a9fddf203?crop=entropy&cs=srgb&fm=jpg&q=85)',
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        >
          <div className="absolute inset-0 bg-slate-900/80" />
          <div className="relative z-10 flex flex-col justify-between p-12 text-white">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-lg bg-amber-700 flex items-center justify-center">
                <Globe className="w-7 h-7" />
              </div>
              <div>
                <h1 className="font-serif text-2xl font-semibold">{appSettings.app_name}</h1>
                <p className="text-slate-400 text-sm">{appSettings.app_byline}</p>
              </div>
            </div>
            
            <div className="space-y-6">
              <h2 className="font-serif text-4xl lg:text-5xl font-semibold leading-tight">
                Managing Your<br />
                <span className="text-amber-500">Real Estate and Investments</span><br />
                with AI Precision
              </h2>
              <p className="text-slate-300 text-lg max-w-md">
                Tracking multiple LLCs, partners, and transactions in one sophisticated platform.
              </p>
              <div className="flex items-center gap-8 pt-4">
                <div className="text-center">
                  <p className="text-3xl font-serif font-bold text-amber-500">{publicStats.llc_count}</p>
                  <p className="text-sm text-slate-400">LLCs Created</p>
                </div>
                <div className="text-center">
                  <p className="text-3xl font-serif font-bold text-amber-500">{publicStats.investor_count}</p>
                  <p className="text-sm text-slate-400">Partners</p>
                </div>
                <div className="text-center">
                  <p className="text-3xl font-serif font-bold text-amber-500">{publicStats.transaction_count}</p>
                  <p className="text-sm text-slate-400">Transactions</p>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-4 text-sm text-slate-400">
              <Building2 className="w-5 h-5" />
              <span>Professional-grade financial management</span>
            </div>
          </div>
        </div>

        {/* Right side - Login Form */}
        <div className="flex-1 flex flex-col items-center justify-center p-8 ledger-background">
          <div className="w-full max-w-md flex-1 flex flex-col justify-center">
            {/* Mobile logo */}
            <div className="lg:hidden flex items-center justify-center gap-3 mb-8">
              <div className="w-12 h-12 rounded-lg bg-slate-900 flex items-center justify-center">
                <Globe className="w-7 h-7 text-amber-500" />
              </div>
              <div>
                <h1 className="font-serif text-2xl font-semibold text-slate-900">{appSettings.app_name}</h1>
                <p className="text-slate-500 text-sm">{appSettings.app_byline}</p>
              </div>
            </div>

            <Card className="border-emerald-300/50 shadow-[0_0_30px_rgba(16,185,129,0.25),0_0_60px_rgba(16,185,129,0.15),0_0_100px_rgba(16,185,129,0.08)] ring-2 ring-emerald-400/40 hover:shadow-[0_0_40px_rgba(16,185,129,0.35),0_0_80px_rgba(16,185,129,0.2),0_0_120px_rgba(16,185,129,0.1)] hover:ring-emerald-400/50 transition-all duration-500">
              {/* Auto logout notice */}
              {wasAutoLoggedOut && (
                <div className="mx-4 mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg" data-testid="auto-logout-notice">
                  <div className="flex items-start gap-2">
                    <Lock className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-amber-800 font-medium text-sm">Session Expired</p>
                      <p className="text-amber-700 text-sm">You were logged out automatically due to inactivity. Please log back in to continue.</p>
                    </div>
                  </div>
                </div>
              )}
              <CardHeader className="space-y-1 pb-6">
                <CardTitle className="text-2xl font-serif">{otpStep ? 'Check your email' : 'Welcome back'}</CardTitle>
                <CardDescription className="text-slate-500">
                  {otpStep ? `We sent a 6-digit code to ${otpMaskedEmail}` : 'Sign in to access your portfolio'}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {otpStep ? (
                <form onSubmit={handleVerifyOtp} className="space-y-4" data-testid="otp-verify-form">
                  <div className="space-y-2">
                    <Label htmlFor="otp-code">Verification code</Label>
                    <Input
                      id="otp-code"
                      type="text"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      placeholder="123456"
                      value={otpCode}
                      onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      maxLength={6}
                      required
                      autoFocus
                      data-testid="otp-code-input"
                      className="h-12 text-center text-2xl tracking-[0.5em] font-mono border-slate-300 focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20"
                    />
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="remember-device"
                      checked={otpRememberDevice}
                      onCheckedChange={setOtpRememberDevice}
                      data-testid="otp-remember-device-checkbox"
                    />
                    <Label htmlFor="remember-device" className="text-sm font-normal cursor-pointer">
                      Remember this device for 30 days
                    </Label>
                  </div>
                  <Button
                    type="submit"
                    className="w-full h-11 bg-slate-900 hover:bg-slate-800 text-white"
                    disabled={loading || otpCode.length < 6}
                    data-testid="otp-verify-btn"
                  >
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : (<>Verify <ArrowRight className="w-4 h-4 ml-2" /></>)}
                  </Button>
                  <div className="flex items-center justify-between text-sm">
                    <button
                      type="button"
                      onClick={handleBackToLogin}
                      className="text-slate-500 hover:text-slate-700 underline-offset-2 hover:underline"
                      data-testid="otp-back-btn"
                    >
                      ← Use a different account
                    </button>
                    <button
                      type="button"
                      onClick={handleResendOtp}
                      disabled={otpResendIn > 0}
                      className={`underline-offset-2 hover:underline ${otpResendIn > 0 ? 'text-slate-400 cursor-not-allowed' : 'text-amber-700 hover:text-amber-800'}`}
                      data-testid="otp-resend-btn"
                    >
                      {otpResendIn > 0 ? `Resend in ${otpResendIn}s` : 'Resend code'}
                    </button>
                  </div>
                </form>
                ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="superadmin@admin.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      data-testid="login-email-input"
                      className="h-11 border-slate-300 focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20 transition-all duration-200"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="password">Password</Label>
                    <div className="relative">
                      <Input
                        id="password"
                        type={showPassword ? "text" : "password"}
                        placeholder="••••••••"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        data-testid="login-password-input"
                        className="h-11 pr-10 border-slate-300 focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20 transition-all duration-200"
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="absolute right-0 top-0 h-11 w-11 hover:bg-transparent"
                        onClick={() => setShowPassword(!showPassword)}
                        tabIndex={-1}
                      >
                        {showPassword ? (
                          <EyeOff className="w-4 h-4 text-slate-500" />
                        ) : (
                          <Eye className="w-4 h-4 text-slate-500" />
                        )}
                      </Button>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox 
                      id="remember" 
                      checked={rememberMe}
                      onCheckedChange={setRememberMe}
                    />
                    <Label htmlFor="remember" className="text-sm font-normal cursor-pointer">
                      Remember me
                    </Label>
                  </div>
                  <Button
                    type="submit"
                    className="w-full h-11 bg-slate-900 hover:bg-slate-800 text-white"
                    disabled={loading}
                    data-testid="login-submit-btn"
                  >
                    {loading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <>
                        Sign in
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </>
                    )}
                  </Button>
                </form>
                )}

                <div className="mt-6 p-4 bg-slate-100 rounded-lg text-center">
                  <p className="text-sm text-slate-600">
                    {appSettings.footer_text} &copy; {new Date().getFullYear()}
                  </p>
                  <p className="text-xs text-slate-500 mt-1">
                    {appSettings.footer_copyright}
                  </p>
                  <p className="text-xs text-slate-400 mt-2 font-mono">
                    Version {APP_VERSION}
                  </p>
                </div>

                {/* Security Info */}
                <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
                  <Badge variant="outline" className="text-xs bg-emerald-50 text-emerald-700 border-emerald-200">
                    <Lock className="w-3 h-3 mr-1" />
                    SSL Secured
                  </Badge>
                  <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200">
                    <Shield className="w-3 h-3 mr-1" />
                    256-bit Encryption
                  </Badge>
                  {clientIP && (
                    <Badge variant="outline" className="text-xs bg-slate-50 text-slate-600 border-slate-200">
                      <Wifi className="w-3 h-3 mr-1" />
                      IP: {clientIP}
                    </Badge>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
